import React, { useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import {
  CardContent,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import LoadingSpinner from "../../../LoadingSpinner";
import {
  useGetAllUserListQuery,
  useGetCallDetailsReportQuery,
  useGetAllQueueQuery,
  useLazyGetCallDetailsReportfetchrecordingQuery,
} from "../../../../Services/reports/tickets/ReportTickets";
import { toast } from "react-toastify";
import CsvDownloader from "react-csv-downloader";

/** Seconds to wait after recording sync API succeeds before refetching call details */
const RECORDING_SYNC_WAIT_SEC = 60;

function formatCountdownMmSs(totalSeconds: number) {
  const s = Math.max(0, Math.floor(totalSeconds));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
}

const ContactDetailReport1 = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [reportType, setReportType] = useState("Inbound"); // Default to Inbound
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const [selectedAssignee, setSelectedAssignee] = useState<any>([]);
  const [theUser, setTheUser] = useState<any>([]);
  const { data: userData, isLoading: userLoading } = useGetAllUserListQuery({});
  const {
    data: queueData,
    isLoading: queueLoading,
    refetch,
  } = useGetAllQueueQuery({ skip: reportType === "Outbound" });
  const [params, setParams] = useState<any>(null);
  const [dataRows, setDataRows] = useState<any[]>([]);
  const [localLoading, setLocalLoading] = useState(false);
  const [allQueue, setAllQueue] = useState<any>([]);
  const [selectedQueues, setSelectedQueues] = useState<any>([]);
  const [options, setOptions] = useState<any>([]);
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [userOptions, setUserOptions] = useState<any[]>([]);
  // const [selectQueue, setSelectQueue] = useState<any>('')
  const [isAllAssigneeSelected, setIsAllAssigneeSelected] = useState(false);

  const {
    data: callsDetailsData,
    isLoading: callsDetailsLoading,
    isFetching: callsDetailsFetching,
    refetch: refetchCallDetails,
  } = useGetCallDetailsReportQuery(params, {
    skip: !params,
  });

  const [triggerFetchRecording, { isFetching: isFetchingRecording }] =
    useLazyGetCallDetailsReportfetchrecordingQuery();

  /** After successful recording sync: countdown until refetch; Submit/refresh disabled while active */
  const [recordingSyncSecondsLeft, setRecordingSyncSecondsLeft] = useState<
    number | null
  >(null);
  const recordingSyncTimerRef = useRef<ReturnType<typeof setInterval> | null>(
    null
  );

  const clearRecordingSyncTimer = () => {
    if (recordingSyncTimerRef.current) {
      clearInterval(recordingSyncTimerRef.current);
      recordingSyncTimerRef.current = null;
    }
  };

  useEffect(() => {
    return () => clearRecordingSyncTimer();
  }, []);

  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
  });

  const renderRecordingCell = (params: { value?: unknown }) => {
    const v = params.value;
    if (v == null || v === "" || v === "-" || v === "No data found") {
      return "-";
    }
    const url = String(v).trim();
    if (!url) return "-";
    return (
      <a href={url} target="_blank" rel="noopener noreferrer">
        Play
      </a>
    );
  };

  const columnsInbound: GridColDef[] = [
    {
      field: "sr_no",
      headerName: "Sr.No",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "account_name",
      headerName: "Account name",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "customer_name",
      headerName: "Customer name",
      minWidth: 150,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "phone_number",
      headerName: "Customer number",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "service_number",
      headerName: "Service number",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "campaign",
      headerName: "Queue name",
      align: "center",
      headerAlign: "center",
    },
    {
      field: "agent_role",
      headerName: "User type",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "agent_name",
      headerName: "User name",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "xlite_no",
      headerName: "Xlite no",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    // {
    //   field: "start_date",
    //   headerName: "Call Start Date",
    //   align: "center",
    //   headerAlign: "center",
    // },
    {
      field: "start_time",
      headerName: "Call time",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "duration",
      headerName: "Duration",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "ringing_duration",
      headerName: "Ringing duration",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "conversation_duration",
      headerName: "Conversation duration",
      minWidth: 160,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "hold_duration",
      headerName: "Hold duration",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "campaign_ivr_duration",
      headerName: "Campaign IVR message",
      minWidth: 160,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "ivr_duration",
      headerName: "IVR duration",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },

    {
      field: "call_duration",
      headerName: "Call duration",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "language",
      headerName: "Language option selected",
      minWidth: 180,
      align: "center",
      headerAlign: "center",
    },
    // {
    //   field: "transfer_duration",
    //   headerName: "Transfer Duration",
    //   align: "center",
    //   headerAlign: "center",
    // },
    // {
    //   field: 'conference_duration',
    //   headerName: "Conference Duration",
    //   align: "center",
    //   headerAlign: "center",
    // },

    {
      field: "hangup_cause",
      headerName: "Hangup cause",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "hangup_sub_cause",
      headerName: "Hangup sub cause",
      minWidth: 150,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "hangup_remark",
      headerName: " Hangup remarks",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "hangup_source",
      headerName: "Hangup source",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    // {
    //   field: "end_date",
    //   headerName: "Call End Date",
    //   align: "center",
    //   headerAlign: "center",
    // },

    {
      field: "end_time",
      headerName: "End time",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "disposition_time",
      headerName: "ACW time",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "handletime",
      headerName: "Handle time",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "recording",
      headerName: "Recording",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
      renderCell: renderRecordingCell,
    },
  ];

  const columnsOutbound: GridColDef[] = [
    {
      field: "sr_no",
      headerName: "Sr.no",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "account_name",
      headerName: "Account name",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "customer_name",
      headerName: "Customer name",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "phone_number",
      headerName: "Customer number",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "service_number",
      headerName: "Service number",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "campaign",
      headerName: "Campaign name",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "agent_role",
      headerName: "User type",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "agent_name",
      headerName: "User name",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "xlite_no",
      headerName: "Xlite no",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "agent_call_status",
      headerName: "Agent call status",
      minWidth: 150,
      align: "center",
      headerAlign: "center",
    },
    // {
    //   field: "start_date",
    //   headerName: "Call Start Date",
    //   align: "center",
    //   headerAlign: "center",
    // },
    {
      field: "start_time",
      headerName: "Call time",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "duration",
      headerName: "Duration",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    // {
    //   field: "transfer_duration",
    //   headerName: "Transfer Duration",
    //   align: "center",
    //   headerAlign: "center",
    // },
    // {
    //   field: 'conference_duration',
    //   headerName: "Conference Duration",
    //   align: "center",
    //   headerAlign: "center",
    // },

    {
      field: "ringing_duration",
      headerName: "Dialing duration",
      minWidth: 150,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "conversation_duration",
      headerName: "Conversation duration",
      minWidth: 150,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "hold_duration",
      headerName: "Hold duration",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "hangup_cause",
      headerName: "Hangup cause",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "hangup_sub_cause",
      headerName: "Hangup sub cause",
      minWidth: 150,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "hangup_remark",
      headerName: "Hangup remarks",
      minWidth: 150,
      align: "center",
      headerAlign: "center",
    },
    // {
    //   field: "hangup_source",
    //   headerName: "Hangup Source",
    //   align: "center",
    //   headerAlign: "center",
    // },
    // {
    //   field: "end_date",
    //   headerName: "Call End Date",
    //   align: "center",
    //   headerAlign: "center",
    // },

    {
      field: "end_time",
      headerName: "End time",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },

    {
      field: "disposition_time",
      headerName: "ACW time",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "handletime",
      headerName: "Handle time",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "recording",
      headerName: "Recording",
      align: "center",
      headerAlign: "center",
      renderCell: renderRecordingCell,
    },
  ];

  const handleReportTypeChange: any = (
    event: React.ChangeEvent<{ value: unknown }>
  ) => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setDataRows([]);
    }, 500); // Small delay to simulate user interaction feedback
    setReportType(event.target.value as string);
    if (event.target.value === "Inbound") {
      refetch();
    }

    // Simulate download completion (due to CsvDownloader's lack of a completion callback)
  };

  const handleDateFromChange = (event: any) => {
    setDateFrom(event.target.value);
  };

  const handleDateToChange = (event: any) => {
    setDateTo(event.target.value);
  };

  // const handleChange: any = (event: React.ChangeEvent<{ value: unknown }>) => {
  //   setSelectedAssignee(event.target.value as string);
  // };
  // const handleQueueChange = (event: any) => {
  //   setSelectQueue(event.target.value);
  // };

  useEffect(() => {
    if (queueData) {
      console.log(queueData, "queueData");
      // Transform queueData to match the expected format
      const transformedQueOptions = queueData.map((item) => ({
        label: item.name, // Use 'name' for the display text
        value: item.id, // Use 'id' as the unique identifier
      }));

      setOptions(transformedQueOptions);
    }
  }, [queueData]);

  useEffect(() => {
    if (userData) {
      console.log(userData, "userData");
      // Transform queueData to match the expected format
      const transformedAssigneeOptions = userData.map((user) => ({
        label: user.username, // Use 'name' for the display text
        value: user.id, // Use 'id' as the unique identifier
      }));

      setUserOptions(transformedAssigneeOptions);
    }
  }, [userData]);

  useEffect(() => {
    const dropdownHeadings = document.querySelectorAll(".dropdown-heading");
    dropdownHeadings.forEach((heading: any) => {
      heading.style.height = "50px";
    });
  }, []);

  // useEffect(() => {

  //   const transformedData = userData?.map((user) => ({
  //     id: user.id,
  //     username: user.username,
  //   }));
  //   if (Array.isArray(userData)) {
  //     setTheUser(transformedData);
  //   } else {
  //     setTheUser([]); // Fallback to an empty array if it's not an array
  //   }
  // }, [userData]);

  // useEffect(() => {
  //   const transformedData = queueData?.map((queue) => ({
  //     id: queue.id,
  //     name: queue.name,
  //   }));
  //   if (queueData && Array.isArray(queueData)) {
  //     setAllQueue(transformedData)
  //   }
  // }, [queueData]);

  const handleSubmit = () => {
    if (!dateFrom) {
      toast.error('Please select "From Date"');
      return;
    }
    if (!dateTo) {
      toast.error('Please select "To Date"');
      return;
    }
    if (selectedAssignee.length === 0) {
      toast.error("Please select Assignee");
      return;
    }

    // if (!selectQueue && reportType === "Inbound") {
    //   toast.error('Please select Queue');
    //   return;
    // }
    if (selectedQueues.length === 0 && reportType === "Inbound") {
      toast.error("Please select a queue");
      return;
    }
    const selectedQueueLabels = selectedQueues.map((queueId) => {
      const queue = options.find((q) => q.value === queueId);
      console.log(queue, "queue");
      return queue ? queue.label : null;
    });
    const selectedAssigneeLabels = selectedAssignee.map((assigneeId) => {
      const assignee = userOptions.find((q) => q.value === assigneeId);
      console.log(assignee, "assignee");
      return assignee ? assignee.value : null;
    });
    const queryParams: any = {
      gte: dateFrom,
      lte: dateTo,
      call_type: reportType === "Inbound" ? "inbound" : "outbound",
      queue: selectedQueueLabels,
      user_id: selectedAssigneeLabels,
    };
    console.log(queryParams, "queuename....");
    // if (selectedAssignee !== "All") {
    //   queryParams.user_id = selectedAssignee; // Add user_id when an assignee is selected
    // }
    // if (selectQueue !== 'All' && reportType === "Inbound") {
    //   queryParams.queue = selectQueue

    // }
    const hadExistingParams = params != null;
    flushSync(() => {
      setParams(queryParams);
    });
    // Same filters = same RTK cache key → no automatic refetch; force reload on repeat Submit
    if (hadExistingParams) {
      void refetchCallDetails();
    }
    console.log(queryParams, "setqueuename");
  };

  function formatDate(datetime) {
    if (!datetime) return "No data found";
    const dateObj = new Date(datetime);
    const day = String(dateObj.getDate()).padStart(2, "0");
    const month = String(dateObj.getMonth() + 1).padStart(2, "0");
    const year = String(dateObj.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
  }

  function formatTime(datetime) {
    if (!datetime) return "No data found";
    const dateObj = new Date(datetime);
    const hours = String(dateObj.getHours()).padStart(2, "0");
    const minutes = String(dateObj.getMinutes()).padStart(2, "0");
    const seconds = String(dateObj.getSeconds()).padStart(2, "0");
    return `${hours}:${minutes}:${seconds}`;
  }
  const formatDuration = (duration) => {
    const parts = duration.split(":").map((part) => part.padStart(2, "0"));
    return parts.join(":");
  };
  useEffect(() => {
    if (Array.isArray(callsDetailsData)) {
      const transformedRows = callsDetailsData.map((item, index) => {
        const commonData = {
          id: index + 1,
          sr_no: index + 1, // Sr.No is a serial number
          account_name: item.contacts[0]?.account?.name || "-", // Assuming account_name doesn't exist in the data
          customer_name: item.contacts[0]?.name || "-", // Assuming customer_name doesn't exist in the data
          phone_number: item.customer_phone_no || "-",
          service_number: item.called_from_phone_no || "-",
          campaign: item.campaign_name || "-",
          agent_role: item.agent_role || "-",
          agent_name: item.agent_name || "-",
          xlite_no: item.extension_no || "-",
          start_time: item.call_start_time_display || "-",
          // start_date: formatDate(item.call_start_time_display) || "No data found",

          // transfer_duration: "No data found",
          // Conference_duration: "No data found",

          duration: item.duration ? formatDuration(item.duration) : "-",
          ringing_duration: item.ringing_seconds || "-",
          conversation_duration: item.conversation_duration
            ? formatDuration(item.conversation_duration)
            : "",
          hold_duration: item.hold_duration_display
            ? formatDuration(item.hold_duration_display)
            : "",
          hangup_cause: item.dispose_remark || "-",
          hangup_sub_cause: item.sub_dispose_remark || "-",
          hangup_remark: item.remarks || "-",
          hangup_source: item.hangup_source || "-",
          end_time: item.call_end_time_display || "-",
          end_date: formatDate(item.call_end_time_display) || "-",
          disposition_time: item.acw_duration_display
            ? formatDuration(item.acw_duration_display)
            : "-",
          handle_time: item.call_duration || "-",
          recording: (() => {
            const link = item.recording_link;
            if (link == null) return "-";
            const s = String(link).trim();
            if (s === "" || s === "null" || s === "undefined") return "-";
            return s;
          })(),
          call_duration: item.call_duration || "-",
          handletime: item.duration ? formatDuration(item.duration) : "-",
        };

        return reportType === "Inbound"
          ? {
              ...commonData,
              campaign_ivr_duration: item.campaign_ivr_msg || "-",
              ivr_duration: item.ivr_duration1 || "-",
              language: item.language || "-",
            }
          : {
              ...commonData,
              agent_call_status: item.agent_outbound_call_status || "-",
            };
      });

      setDataRows(transformedRows);
      setLocalLoading(false);
    } else {
      setDataRows([]); // Reset if data is not an array
      setLocalLoading(false);
    }

    setLocalLoading(false);
  }, [callsDetailsData, reportType]);

  const downloaderRef = useRef(null);

  const resetFilters = () => {
    setDateFrom("");
    setDateTo("");
    setParams(null);
    setSelectedQueues([]);
    setSelectedAssignee([]);
  };

  const isRecordingSyncCooldown =
    recordingSyncSecondsLeft !== null && recordingSyncSecondsLeft > 0;

  const handleRefreshReport = async () => {
    if (!params) {
      toast.warning(
        "Submit your filters first. If recordings or report rows are still missing, try refresh."
      );
      return;
    }
    if (isRecordingSyncCooldown) return;

    try {
      const raw = (await triggerFetchRecording(params).unwrap()) as {
        message?: string;
        data?: { message?: string };
      };
      const syncMessage =
        (typeof raw?.message === "string" && raw.message) ||
        (typeof raw?.data?.message === "string" && raw.data.message) ||
        null;
      toast.success(syncMessage ?? "Recording sync started.");

      clearRecordingSyncTimer();
      setRecordingSyncSecondsLeft(RECORDING_SYNC_WAIT_SEC);

      recordingSyncTimerRef.current = setInterval(() => {
        setRecordingSyncSecondsLeft((prev) => {
          if (prev === null || prev <= 0) return null;
          if (prev <= 1) {
            clearRecordingSyncTimer();
            void refetchCallDetails().then(() => {
              toast.success("Call details refreshed.");
            });
            return null;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err: any) {
      const msg =
        err?.data?.message ||
        err?.data?.detail ||
        err?.error ||
        "Failed to refresh recordings.";
      toast.error(typeof msg === "string" ? msg : "Failed to refresh recordings.");
    }
  };

  const ITEM_HEIGHT = 60;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 150,
      },
    },
  };

  const handlePaginationChange = (newPaginationModel: any) => {
    setLocalLoading(true);
    setPaginationModel(newPaginationModel);
    setTimeout(() => {
      setLocalLoading(false);
    }, 1000);
  };

  const showFullPageSpinner =
    userLoading ||
    callsDetailsLoading ||
    queueLoading ||
    (!!params && callsDetailsFetching && dataRows.length === 0);

  console.log(allQueue);
  const handleQueueChange = (event) => {
    const {
      target: { value },
    } = event;
    if (value.includes("all")) {
      setIsAllSelected(!isAllSelected);
      setSelectedQueues(
        isAllSelected ? [] : options.map((option) => option.value)
      );
    } else {
      setSelectedQueues(value);
      setIsAllSelected(value.length === options.length);
    }
  };

  const handleAssigneeChange = (event) => {
    const {
      target: { value },
    } = event;
    if (value.includes("all")) {
      setIsAllAssigneeSelected(!isAllAssigneeSelected);
      setSelectedAssignee(
        isAllAssigneeSelected ? [] : userOptions.map((option) => option.value)
      );
    } else {
      setSelectedAssignee(value);
      setIsAllAssigneeSelected(value.length === userOptions.length);
    }
  };

  return (
    <div id="kt_app_content_container" className="app-container mx-5">
      <div className="card card-flush">
        {/* Card Header */}
        <div
          className="card-header align-items-center py-1 gap-2 gap-md-5"
          style={{ justifyContent: "flex-start" }}
        >
          {/* Back button */}
          {/* <img
            src="https://insurance.circleonecrm.com/assets/arrow_back.svg"
            alt="Back"
            style={{ marginBottom: "-2px", cursor: "pointer" }}
            className="ng-star-inserted"
            onClick={() => {
              navigate("/reports/telephony/reports-list");
            }}
          /> */}
          <button
            className="btn btn-link mb-2"
            onClick={() => {
              navigate("/reports/telephony/reports-list");
            }}
            style={{ marginRight: "-17px" }}
          >
            <i
              className="bi bi-arrow-90deg-left"
              style={{
                fontSize: "20px",
                color: "black",
                cursor: "pointer",
              }}
            ></i>
          </button>
          <span className="fs-3 text-gray-800 fw-bold ms-3"> Call details</span>
        </div>

        {/* Toolbar */}
        <div
          className="card-toolbar flex-row-fluid justify-content-start gap-5 d-flex mb-10"
          style={{ marginLeft: "30px" }}
        >
          <div className="w-100 mw-150px">
            <TextField
              id="date-from"
              label="From"
              type="date"
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                inputProps: {
                  max: new Date().toISOString().split("T")[0], // Restrict to today or earlier
                  style: { textTransform: "uppercase" }, // Optional: uppercase input text
                },
              }}
              fullWidth
              value={dateFrom}
              onChange={handleDateFromChange}
            />
          </div>
          <div className="w-100 mw-150px">
            <TextField
              id="date-to"
              label="To"
              type="date"
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                inputProps: {
                  max: new Date().toISOString().split("T")[0],
                  style: { textTransform: "uppercase" }, // Optional: uppercase input text
                },
              }}
              fullWidth
              value={dateTo}
              onChange={handleDateToChange}
            />
          </div>

          <div className="w-100 mw-150px">
            <FormControl fullWidth>
              <InputLabel id="select-type">Select Type</InputLabel>
              <Select
                labelId="select-type"
                id="select-type"
                label="Select Type"
                value={reportType}
                onChange={handleReportTypeChange}
              >
                <MenuItem value="Inbound">Inbound</MenuItem>
                <MenuItem value="Outbound">Outbound</MenuItem>
              </Select>
            </FormControl>
          </div>
          {/* <div className="w-100 mw-150px">
            <FormControl fullWidth>
              <InputLabel id="Assignee-select-label">Assignee</InputLabel>
              <Select labelId="Assignee-select-label" id="Assignee-select" label="Assignee" MenuProps={MenuProps} value={selectedAssignee}
                onChange={handleChange}>
                <MenuItem key="all" value="All">
                  All
                </MenuItem>
                {theUser?.map((assignee: any) => (
                  <MenuItem key={assignee.id} value={assignee.id}>
                    {assignee.username}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div> */}
          <div className="w-100 mw-150px">
            <FormControl fullWidth>
              <InputLabel id="assignee-select-label">Assignee</InputLabel>
              <Select
                labelId="assignee-select-label"
                id="assignee-select"
                multiple
                value={selectedAssignee}
                onChange={handleAssigneeChange}
                input={<OutlinedInput label="Assignee" />}
                renderValue={(selected) => {
                  if (isAllAssigneeSelected) {
                    return <div>All</div>;
                  }
                  return (
                    <div>
                      {selected
                        .map((assigneeId) => {
                          const assignee = userOptions.find(
                            (q) => q.value === assigneeId
                          );
                          return assignee ? assignee.label : null;
                        })
                        .join(", ")}
                    </div>
                  );
                }}
                MenuProps={MenuProps}
              >
                <MenuItem
                  value="all"
                  onClick={() =>
                    setIsAllAssigneeSelected(!isAllAssigneeSelected)
                  }
                >
                  <Checkbox
                    checked={isAllAssigneeSelected}
                    indeterminate={
                      selectedAssignee.length > 0 && !isAllAssigneeSelected
                    }
                  />
                  <ListItemText primary="All" />
                </MenuItem>
                {userOptions.map((assignee) => (
                  <MenuItem key={assignee.value} value={assignee.value}>
                    <Checkbox
                      checked={selectedAssignee.indexOf(assignee.value) > -1}
                    />
                    <ListItemText primary={assignee.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          {reportType === "Inbound" && (
            // <div className="w-100 mw-150px">
            //   <FormControl fullWidth>
            //     <InputLabel id="Queue-select-label">Queue</InputLabel>
            //     <Select
            //       labelId="Queue-select-label"
            //       id="Queue-select"
            //       label="Queue"
            //       value={selectQueue}
            //       onChange={handleQueueChange}
            //       MenuProps={MenuProps}
            //     >
            //       {" "}
            //       <MenuItem key="all" value="All">
            //         All
            //       </MenuItem>
            //       {allQueue?.map((queue: any) => (

            //         <MenuItem key={queue.id} value={queue.id}>{queue.name}</MenuItem>
            //       ))}
            //     </Select>
            //   </FormControl>
            // </div>
            <div className="w-100 mw-150px">
              <FormControl fullWidth>
                <InputLabel id="queue-select-label">Select Queues</InputLabel>
                <Select
                  labelId="queue-select-label"
                  id="queue-select"
                  multiple
                  value={selectedQueues}
                  onChange={handleQueueChange}
                  input={<OutlinedInput label="Select Queues" />}
                  renderValue={(selected) => {
                    if (isAllSelected) {
                      return <div>All</div>;
                    }
                    return (
                      <div>
                        {selected
                          .map((queueId) => {
                            const queue = options.find(
                              (q) => q.value === queueId
                            );
                            return queue ? queue.label : null;
                          })
                          .join(", ")}
                      </div>
                    );
                  }}
                  MenuProps={MenuProps}
                >
                  <MenuItem
                    value="all"
                    onClick={() => setIsAllSelected(!isAllSelected)}
                  >
                    <Checkbox
                      checked={isAllSelected}
                      indeterminate={
                        selectedQueues.length > 0 && !isAllSelected
                      }
                    />
                    <ListItemText primary="All" />
                  </MenuItem>
                  {options.map((queue) => (
                    <MenuItem key={queue.value} value={queue.value}>
                      <Checkbox
                        checked={selectedQueues.indexOf(queue.value) > -1}
                      />
                      <ListItemText primary={queue.label} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          )}

          <div className="ms-auto d-flex gap-3 align-items-center flex-wrap" style={{ marginRight: "20px" }}>
            <button
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={isRecordingSyncCooldown}
              title={
                isRecordingSyncCooldown
                  ? "Wait for recording sync countdown to finish"
                  : undefined
              }
            >
              Submit
            </button>

            {/* CsvDownloader component outside the button */}
            <button className="btn btn-primary">
              <CsvDownloader
                ref={downloaderRef}
                filename={`${reportType}-call-details-report`}
                datas={dataRows}
                columns={(reportType === "Inbound"
                  ? columnsInbound
                  : columnsOutbound
                ).map((col) => ({
                  id: col.field,
                  displayName: col.headerName,
                }))}
                separator=","
                wrapColumnChar={`"`}
              >
                Download
              </CsvDownloader>
            </button>

            <button
              type="button"
              className="btn btn-link"
              onClick={resetFilters}
              aria-label="Reset filters"
            >
              <i className="bi bi-funnel fs-2 text-dark"></i>
            </button>

            <Tooltip
              title={
                isRecordingSyncCooldown && recordingSyncSecondsLeft != null
                  ? `Wait ${formatCountdownMmSs(recordingSyncSecondsLeft)} — the report will refresh automatically.`
                  : "If you are missing call recordings or report details, click refresh. After sync, wait 1 minute for the report to refresh."
              }
              arrow
              placement="top"
              enterDelay={300}
            >
              <span className="d-inline-flex">
                <button
                  type="button"
                  className="btn btn-link p-1 d-inline-flex flex-column align-items-center justify-content-center gap-0"
                  onClick={handleRefreshReport}
                  disabled={
                    !params ||
                    callsDetailsLoading ||
                    callsDetailsFetching ||
                    isFetchingRecording ||
                    isRecordingSyncCooldown
                  }
                  aria-label={
                    isRecordingSyncCooldown && recordingSyncSecondsLeft != null
                      ? `Refresh report, ${formatCountdownMmSs(recordingSyncSecondsLeft)} remaining`
                      : "Refresh report"
                  }
                >
                  {isFetchingRecording ||
                  (callsDetailsFetching && !isRecordingSyncCooldown) ? (
                    <span
                      className="spinner-border spinner-border-sm text-primary"
                      role="status"
                      style={{ width: "1.25rem", height: "1.25rem" }}
                    />
                  ) : (
                    <i className="bi bi-arrow-clockwise fs-2 text-primary" aria-hidden />
                  )}
                  {isRecordingSyncCooldown && recordingSyncSecondsLeft != null && (
                    <span
                      className="small text-primary fw-semibold lh-1 mt-1"
                      style={{
                        fontVariantNumeric: "tabular-nums",
                        letterSpacing: "0.02em",
                      }}
                    >
                      {formatCountdownMmSs(recordingSyncSecondsLeft)}
                    </span>
                  )}
                </button>
              </span>
            </Tooltip>
          </div>
        </div>

        <CardContent style={{ width: "100%" }}>
          {showFullPageSpinner ? (
            <LoadingSpinner />
          ) : (
            <DataGrid
              rows={dataRows}
              columns={
                reportType === "Inbound" ? columnsInbound : columnsOutbound
              }
              pagination
              paginationModel={paginationModel}
              onPaginationModelChange={handlePaginationChange}
              loading={
                localLoading || (!!params && callsDetailsFetching)
              }
              disableColumnMenu
              pageSizeOptions={[5, 10, 15, 20]}
              sx={{
                "& .MuiDataGrid-columnHeader": {
                  backgroundColor: "lightgray",
                  "&:last-child": {
                    justifyContent: "flex-end",
                  },
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontWeight: "bold",
                },
                "& .MuiDataGrid-columnsContainer": {
                  backgroundColor: "lightgray",
                },
                // Target the pagination select and actions by stable component classes
                "& .MuiTablePagination-select, .MuiTablePagination-actions": {
                  marginBottom: "10px",
                },
                // Target the DataGrid overlay wrapper with its stable class
                "& .MuiDataGrid-overlayWrapper": {
                  height: "50px",
                },
              }}
            />
          )}
        </CardContent>
      </div>
    </div>
  );
};

export default ContactDetailReport1;